import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@utils/classNames';
import { ParameterDto, SampleFormat } from '@src/types/types';
import { useParameterStatus } from '@hooks/useParameterStatus.ts';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';
import { getNumericValue } from '@utils/parameters.ts';
import { usePrintContext } from '@contexts/PrintContext';
import { ParameterListItemLeft } from './ParameterListItemLeft';
import { ParameterListItemMiddle } from './ParameterListItemMiddle';
import { ParameterListItemRight } from './ParameterListItemRight';
import { ParameterListItemExpandedContent } from './ParameterListItemExpandedContent';

export const ParameterListItem = ({
  parameter,
}: PropsWithChildren<{ parameter: ParameterDto }>) => {
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });
  const { name, format, category, regulations, value, unit } = parameter;
  const numericValue = getNumericValue(parameter);
  const [isExpanded, setIsExpanded] = useState(false);
  const { isPrinting } = usePrintContext();
  const { exceededRegulation, status } = useParameterStatus(
    parameter,
    format,
    numericValue,
    regulations,
  );
  const showBar =
    parameter.format === SampleFormat.NUMERIC &&
    !regulations.some((regulation) => regulation.lowerLimit);

  const statusBorderColor = {
    error: 'border-error',
    warning: 'border-ivarioYellow',
    success: '',
    unset: '',
    notAnalyzable: 'border-grey3light',
  }[status];

  return (
    <div
      className={classNames(
        'rounded-bonusLarge bg-grey3light print:break-inside-avoid',
        status !== ParameterStatusType.UNSET &&
          status !== ParameterStatusType.SUCCESS &&
          'border-2',
        statusBorderColor,
      )}
      data-testid="parameter-list-item"
    >
      <div
        className={classNames(
          'flex flex-row items-center p-2.5 md:p-5 print:p-5 space-x-2 md:space-x-8 xl:justify-start print:justify-start justify-between',
          regulations.length > 1 && showBar && 'md:pb-8',
          'cursor-pointer',
        )}
        onClick={(e) => {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }}
        data-testid="parameter-list-item-header"
      >
        <ParameterListItemLeft
          subtitle={category}
          title={name ?? 'Unknown'}
          parameter={parameter}
        />
        <ParameterListItemMiddle parameter={parameter} />
        <ParameterListItemRight
          isExpanded={isPrinting || isExpanded}
          status={parameter.notAnalyzable ? ParameterStatusType.UNSET : status}
          value={
            parameter.nonDetectable
              ? `${t('notDetectableLong')}: ${value} ${unit}`
              : parameter.notAnalyzable
                ? t('notAnalyzableLong')
                : `${value} ${unit}`
          }
        />
      </div>
      {(isPrinting || isExpanded) && (
        <ParameterListItemExpandedContent
          parameter={parameter}
          exceededRegulation={exceededRegulation}
          status={status}
        />
      )}
    </div>
  );
};
